// Kid shell — playful, kid-safe. Big colorful bottom nav, large touch targets
// (≥48px), generous rounding. No analytics widgets, no support chat
// affordance (CLAUDE.md §29). Sign-out lives in a small top-corner control so
// it's reachable but not prominent.

import { useLocale, useT } from '@fajrarabi/i18n/react';
import { Link, useParams, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { LangSwitcher } from '../LangSwitcher';
import { NotificationBell } from '../NotificationBell';

import { useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';

interface KidNavItem {
  labelKey: string;
  icon: string;
  /** Routable items link; the rest are visible-but-inert (land in later phases). */
  to?: '/$locale' | '/$locale/messages' | '/$locale/tasks' | '/$locale/badges';
}

const NAV: KidNavItem[] = [
  { labelKey: 'app.nav.today', icon: '🌟', to: '/$locale' },
  { labelKey: 'app.nav.tasks', icon: '✅', to: '/$locale/tasks' },
  { labelKey: 'app.nav.badges', icon: '🏅', to: '/$locale/badges' },
  { labelKey: 'app.nav.messages', icon: '💬', to: '/$locale/messages' },
];

export function KidShell({ children }: { children: ReactNode }) {
  const t = useT();
  const { isRtl } = useLocale();
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);
  const { user, signOut } = useAuth();

  // Immersive mode for the lesson player: hide the chrome (header + bottom nav)
  // so a single activity fills the screen and a kid can't wander mid-lesson.
  // The player has its own exit affordance.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const immersive = pathname.includes('/lesson/');

  if (immersive) {
    return (
      <div className="min-h-dvh bg-cream-100 px-1 py-2" dir={isRtl ? 'rtl' : 'ltr'}>
        {children}
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-cream-100" dir={isRtl ? 'rtl' : 'ltr'}>
      <header className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-sun-300 text-2xl shadow-sm">
            🦊
          </span>
          <span className="text-lg font-bold text-burgundy-700">
            {user?.displayName ?? user?.username}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell variant="kid" />
          <LangSwitcher locale={locale} ariaLabel={t('app.common.lang_switch_label')} />
          <button
            type="button"
            onClick={() => void signOut()}
            className="grid h-11 w-11 place-items-center rounded-full bg-cream-50 text-xl ring-1 ring-burgundy-500/10 transition-colors hover:bg-cream-200"
            aria-label={t('app.common.sign_out')}
          >
            🚪
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-auto px-5 pb-28 pt-2">{children}</main>

      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex items-stretch justify-around border-t-4 border-sun-300 bg-cream-50 px-2 py-2 shadow-[0_-8px_24px_-12px_rgba(31,26,23,0.2)]"
        aria-label={t('app.common.app_name')}
      >
        {NAV.map((item) =>
          item.to ? (
            <Link
              key={item.labelKey}
              to={item.to}
              params={{ locale }}
              activeOptions={{ exact: true }}
              className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-2 py-1 text-burgundy-700 transition-colors hover:bg-cream-100 [&.active]:bg-sun-50 [&.active]:text-sun-600"
              activeProps={{ className: 'active' }}
            >
              <span className="text-2xl" aria-hidden="true">
                {item.icon}
              </span>
              <span className="text-xs font-bold">{t(item.labelKey)}</span>
            </Link>
          ) : (
            <span
              key={item.labelKey}
              aria-disabled="true"
              className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-2 py-1 text-ink-500/50"
            >
              <span className="text-2xl" aria-hidden="true">
                {item.icon}
              </span>
              <span className="text-xs font-bold">{t(item.labelKey)}</span>
            </span>
          ),
        )}
      </nav>
    </div>
  );
}
